<template>
  <div v-if="show" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">{{ $t('web3.connect_wallet') }}</h2>
        <button class="close-button" @click="closeModal">
          <s-icon color="black" width="24">close</s-icon>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="wallet-options">
          <button 
            class="wallet-option"
            @click="connectWallet('metamask')"
            :disabled="isConnecting"
          >
            <div class="wallet-icon">
              <img src="/img/icons/metamask.svg" alt="MetaMask" class="wallet-logo">
            </div>
            <div class="wallet-details">
              <div class="wallet-name">MetaMask</div>
              <div class="wallet-description">{{ $t('web3.metamask_description') }}</div>
            </div>
            <div class="wallet-status">
              <s-icon v-if="isConnecting" color="black" width="20" class="spinning">cog</s-icon>
              <s-icon v-else color="black" width="20">chevron-right</s-icon>
            </div>
          </button>

          <button 
            class="wallet-option"
            @click="connectWallet('walletconnect')"
            :disabled="isConnecting"
          >
            <div class="wallet-icon">
              <img src="/img/icons/walletconnect.svg" alt="WalletConnect" class="wallet-logo">
            </div>
            <div class="wallet-details">
              <div class="wallet-name">WalletConnect</div>
              <div class="wallet-description">{{ $t('web3.walletconnect_description') }}</div>
            </div>
            <div class="wallet-status">
              <s-icon v-if="isConnecting" color="black" width="20" class="spinning">cog</s-icon>
              <s-icon v-else color="black" width="20">chevron-right</s-icon>
            </div>
          </button>

          <button 
            class="wallet-option"
            @click="connectWallet('coinbase')"
            :disabled="isConnecting"
          >
            <div class="wallet-icon">
              <img src="/img/icons/coinbase.svg" alt="Coinbase Wallet" class="wallet-logo">
            </div>
            <div class="wallet-details">
              <div class="wallet-name">Coinbase Wallet</div>
              <div class="wallet-description">{{ $t('web3.coinbase_description') }}</div>
            </div>
            <div class="wallet-status">
              <s-icon v-if="isConnecting" color="black" width="20" class="spinning">cog</s-icon>
              <s-icon v-else color="black" width="20">chevron-right</s-icon>
            </div>
          </button>
        </div>

        <div v-if="error" class="error-message">
          <s-icon color="red" width="20">alert-circle-outline</s-icon>
          <span>{{ error }}</span>
        </div>

        <div class="help-text">
          <p>{{ $t('web3.help_text') }}</p>
          <p>{{ $t('web3.help_text_2') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();

const show = computed(() => store.state.modals.web3Modal);
const isConnecting = computed(() => store.state.web3.isConnecting);
const error = computed(() => store.state.web3.error);

const closeModal = () => {
  store.commit('modals/setWeb3Modal', false);
  store.commit('web3/SET_ERROR', null);
};

const connectWallet = async (walletType) => {
  try {
    await store.dispatch('web3/connectWallet', walletType);
    if (store.state.web3.isConnected) {
      closeModal();
    }
  } catch (err) {
    console.error('Failed to connect wallet:', err);
  }
};
</script>

<style scoped lang="less">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  background-color: var(--white);
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid var(--shade4);
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--black);
}

.close-button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ease 0.2s;

  &:hover {
    background-color: var(--shade2);
  }
}

.modal-body {
  padding: 24px;
}

.wallet-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

  .wallet-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 2px solid var(--shade4);
    border-radius: 12px;
    background-color: var(--white);
    cursor: pointer;
    transition: all ease 0.2s;
    text-align: left;
    min-height: 80px;

    &:hover:not(:disabled) {
      border-color: var(--primary);
      background-color: var(--shade2);
    }

    &:focus {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

.wallet-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: var(--shade2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wallet-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.wallet-details {
  flex-grow: 1;
  min-width: 0;
}

.wallet-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--black);
  margin-bottom: 4px;
}

.wallet-description {
  font-size: 14px;
  color: var(--shade6);
  line-height: 1.4;
}

.wallet-status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: var(--red);
  font-size: 14px;
  margin-bottom: 20px;
}

.help-text {
  padding: 16px;
  background-color: var(--shade2);
  border-radius: 8px;
  font-size: 14px;
  color: var(--shade6);
  line-height: 1.5;

  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

@media only screen and (max-width: 576px) {
  .modal-overlay {
    padding: 16px;
  }

  .modal-content {
    max-height: 95vh;
    max-width: calc(100vw - 32px);
    margin: 0 16px;
    border-radius: 12px;
  }

  .modal-header {
    padding: 20px 20px 12px 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .wallet-option {
    padding: 14px;
    gap: 12px;
    min-height: 70px;
  }

  .wallet-icon {
    width: 40px;
    height: 40px;
  }

  .wallet-logo {
    width: 28px;
    height: 28px;
  }

  .wallet-name {
    font-size: 14px;
  }

  .wallet-description {
    font-size: 12px;
    line-height: 1.3;
  }

  .modal-title {
    font-size: 18px;
  }

  .error-message {
    padding: 10px 14px;
    font-size: 13px;
  }

  .help-text {
    padding: 14px;
    font-size: 13px;
  }
}
</style>
